import React, { FC } from 'react';

import type { Locale } from '@rover/constants/src/i18n/lingui.constants';
import GroomingHowBrandWorks from '@rover/react-lib/src/pages/landing/SeoLocation/templates/GroomingSeoLocation/components/GroomingHowBrandWorks';
import GroomingSeoLocationResults from '@rover/react-lib/src/pages/landing/SeoLocation/templates/GroomingSeoLocation/components/GroomingSeoLocationResults';
import {
  appShowcaseGroomingImage,
  groomingQuickFactSticker,
} from '@rover/react-lib/src/pages/landing/SeoLocation/templates/GroomingSeoLocation/images';
import { getGroomingSearchPageUrl } from '@rover/react-lib/src/pages/landing/SeoLocation/templates/GroomingSeoLocation/utils/groomingSearch';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import { ServiceLinkWithName } from '@rover/react-lib/src/redux/ducks/serviceLinks.duck';
import type { BrandLocalityServiceCitiesItem } from '@rover/rsdk/src/apiClient/latest/model/brandLocalityServiceCitiesItem';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { CountryFooterLink } from '@rover/rsdk/src/apiClient/latest/model/countryFooterLink';
import type { GroomingSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/groomingSearchResponse';
import type { LocalizedBrandService } from '@rover/rsdk/src/apiClient/latest/model/localizedBrandService';
import type { PaginatedCountryRegionLinkList } from '@rover/rsdk/src/apiClient/latest/model/paginatedCountryRegionLinkList';
import type { ServiceLandingPathDescriptor } from '@rover/shared/js/hooks/useServiceLanding';
import getBrandDisplayName from '@rover/shared/js/utils/getBrandDisplayName';

import QuickFact from '../../../components/QuickFact';
import servicesLinksImage from '../../components/images/dog-looking-upward.png';
import RoverProtectCondensed from '../../components/RoverProtectCondensed';
import ServiceLandingFaqList from '../../components/ServiceLandingFaqList';
import ServiceLandingFooterLinks from '../../components/ServiceLandingFooterLinks';
import ServiceLandingPageWrapper from '../../components/ServiceLandingPageWrapper';
import ServiceLandingSection, {
  SERVICE_LANDING_SECTION_MARGIN_Y,
} from '../../components/ServiceLandingSection';
import ServiceLandingServicesWrapper from '../../components/ServiceLandingServicesWrapper';

import GroomingServiceBenefits from './components/GroomingServiceBenefits';
import GroomingServiceLandingHero from './components/GroomingServiceLandingHero';
import {
  getSearchParamsFromQuery,
  GROOMING_LANDING_SEARCH_PARAMS,
} from './utils/groomingLandingSearch';

export type GroomingServiceLandingPageTemplateProps = {
  serviceLandingData: LocalizedBrandService;
  descriptor: ServiceLandingPathDescriptor;
  serviceLinksWithDisplayName: ServiceLinkWithName[];
  countryConfig: CountryConfiguration;
  locale: Locale;
  groomingSearchResponse?: GroomingSearchResponse;
  citiesAndNeighborhoodsLinks?: BrandLocalityServiceCitiesItem[];
  countryFooterLinks?: CountryFooterLink[];
  countryRegionLinks: PaginatedCountryRegionLinkList;
};

const GroomingServiceLandingPageTemplate: FC<GroomingServiceLandingPageTemplateProps> = ({
  serviceLandingData,
  descriptor,
  serviceLinksWithDisplayName,
  countryConfig,
  locale,
  groomingSearchResponse,
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
}) => {
  const {
    h1,
    h2,
    seoTitle,
    seoMetaDescription,
    internalServiceType,
    indexable,
    serviceBenefitsHeading,
    serviceBenefitItems,
  } = serviceLandingData;
  const brandName = getBrandDisplayName();

  const searchParams = groomingSearchResponse
    ? getSearchParamsFromQuery(groomingSearchResponse.query)
    : GROOMING_LANDING_SEARCH_PARAMS;
  const searchPageUrl = getGroomingSearchPageUrl(searchParams);

  return (
    <ServiceLandingPageWrapper
      seoTitle={seoTitle}
      seoMetaDescription={seoMetaDescription}
      indexable={indexable}
      serviceTypeName={internalServiceType.displayName}
      brandName={brandName}
      h1={h1}
      h2={h2}
      countryConfig={countryConfig}
    >
      <GroomingServiceLandingHero h1={h1} h2={h2} searchParams={searchParams} />

      <RoverProtectCondensed
        countryConfig={countryConfig}
        isGrooming
        mb={SERVICE_LANDING_SECTION_MARGIN_Y}
      />

      <GroomingServiceBenefits
        serviceBenefitsHeading={serviceBenefitsHeading}
        serviceBenefitItems={serviceBenefitItems}
      />

      {groomingSearchResponse && (
        <GroomingSeoLocationResults
          heading="Find pet grooming near you"
          searchResults={groomingSearchResponse.results}
          otherGroomers={groomingSearchResponse.otherGroomers}
          searchPageUrl={searchPageUrl}
          currencyCode={countryConfig.currencyCode}
          locale={locale}
        />
      )}

      <QuickFact themeColor="blue">
        {`Pet parents rate ${brandName} groomers 4.9 stars on average.`}
      </QuickFact>

      <GroomingHowBrandWorks />

      <QuickFact themeColor="green" stickerImageSrc={groomingQuickFactSticker} stickerBottom="-23%">
        A private appointment, just for your dog, at your home or your groomer’s.
      </QuickFact>

      <ServiceLandingServicesWrapper
        serviceLandingType={null}
        serviceLinksWithDisplayName={serviceLinksWithDisplayName}
        imageSrc={servicesLinksImage}
      >
        Rover offers many services to meet your pet care needs, including:
      </ServiceLandingServicesWrapper>

      <ServiceLandingFaqList descriptor={descriptor} title="Pet grooming FAQs" />

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout altImageSrc={appShowcaseGroomingImage} imageMaxWidth="50%" />
      </ServiceLandingSection>

      <ServiceLandingFooterLinks
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
      />
    </ServiceLandingPageWrapper>
  );
};

export default GroomingServiceLandingPageTemplate;
